import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useDevelopmentPlanApi from './config';
import IdpFlowBar from '../my-development-plans/IdpFlowBar';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams, formatDate } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import Tabs from '../../components/ui/Tabs';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import api from '../../services/api';

const Container = styled.div``;

const HeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 680px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const DialogTitle = styled.h3`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 140px;
`;

const DetailValue = styled.span`
  color: ${({ theme }) => theme.colors.text.dark?.little || theme.colors.text.primary};
`;

const GoalCard = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ theme }) => `${theme.colors.secondary.main}1A`};
  color: ${({ theme }) => theme.colors.secondary.dark};
  margin-right: 8px;
`;

const ActionCard = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 8px;
  font-size: 13px;
`;

const ReadyBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ theme }) => `${theme.colors.auxiliar.success}1A`};
  color: ${({ theme }) => theme.colors.auxiliar.success};
  margin-left: 8px;
`;

function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      // EmployeeController yalnız firstName/lastName/fullName [like] bind ediyor —
      // `name[like]` no-op'tu (arama filtrelemiyordu). Bkz. feedback-360/config.
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

const employeeAsync = buildEmployeeAsync();

export default function DevelopmentPlansPage() {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('TD');

  const [activeTab, setActiveTab] = useState('all');
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [detailModal, setDetailModal] = useState({ isOpen: false, plan: null });
  const [detailLoading, setDetailLoading] = useState(false);

  // Görüşmeyi Kaydet (meeting -> manager_approval)
  const [meetingModal, setMeetingModal] = useState({ isOpen: false, planId: null, title: '' });
  const [managerSupport, setManagerSupport] = useState('');
  const [meetingBusy, setMeetingBusy] = useState(false);

  // Onayla / Revizyon İste (manager_approval -> active | revision_required)
  const [revisionModal, setRevisionModal] = useState({ isOpen: false, planId: null, title: '' });
  const [revisionNote, setRevisionNote] = useState('');
  const [revisionBusy, setRevisionBusy] = useState(false);

  // Dönem Sonu Değerlendirmeyi Tamamla (period_end_review -> completed)
  const [completeReviewModal, setCompleteReviewModal] = useState({ isOpen: false, planId: null, title: '' });
  const [employeeReviewNote, setEmployeeReviewNote] = useState('');
  const [managerReviewNote, setManagerReviewNote] = useState('');
  const [completeReviewBusy, setCompleteReviewBusy] = useState(false);

  // İK Daveti (org-wide, dönem bağlı değilse)
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteEmployeeIds, setInviteEmployeeIds] = useState([]);
  const [inviteBusy, setInviteBusy] = useState(false);

  const {
    fetching, data, pagination, loadFailed, columns: baseColumns,
    fetchingPending, pendingData, pendingLoadFailed, pendingColumns: basePendingColumns,
    getById, recordMeeting, decide, startPeriodEndReview, completeReview, invite,
  } = useDevelopmentPlanApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const openDetailModal = useCallback(async (row) => {
    setDetailModal({ isOpen: true, plan: row });
    setDetailLoading(true);
    const detail = await getById(row.id);
    if (detail) setDetailModal({ isOpen: true, plan: detail });
    setDetailLoading(false);
  }, [getById]);

  // PM analitik P5 (E12) — Gelişim ve PIP analitiği plan listesinden derin bağlantı: ?planId= plan detayını açar
  // (okuma kapısı aynı: GET v1/development-plans/{id}, TD.Read + ekip kapsamı).
  // Gelen Kutum da (idp_approval kartı) buraya bağlanır; detay kapanınca parametre silinir (useDeepLinkParams).
  const { values: { planId: linkedPlanId }, clear: clearDeepLink } = useDeepLinkParams(['planId']);
  useEffect(() => {
    if (linkedPlanId) openDetailModal({ id: linkedPlanId });
  }, [linkedPlanId, openDetailModal]);

  // ---- Görüşmeyi Kaydet ----
  const openMeetingModal = useCallback((row) => {
    setManagerSupport('');
    setMeetingModal({ isOpen: true, planId: row.id, title: row.title });
  }, []);

  const handleMeetingConfirm = useCallback(async () => {
    if (!meetingModal.planId) return;
    setMeetingBusy(true);
    await recordMeeting(meetingModal.planId, { managerSupport: managerSupport || undefined });
    setMeetingModal({ isOpen: false, planId: null, title: '' });
    setMeetingBusy(false);
  }, [meetingModal.planId, managerSupport, recordMeeting]);

  // ---- Onayla ----
  const handleApprove = useCallback(async (row) => {
    await decide(row.id, { decision: 'approve' });
  }, [decide]);

  // ---- Revizyon İste ----
  const openRevisionModal = useCallback((row) => {
    setRevisionNote('');
    setRevisionModal({ isOpen: true, planId: row.id, title: row.title });
  }, []);

  const handleRevisionConfirm = useCallback(async () => {
    if (!revisionModal.planId || !revisionNote.trim()) return;
    setRevisionBusy(true);
    await decide(revisionModal.planId, { decision: 'revision', note: revisionNote });
    setRevisionModal({ isOpen: false, planId: null, title: '' });
    setRevisionBusy(false);
  }, [revisionModal.planId, revisionNote, decide]);

  // ---- Dönem Sonu Değerlendirmeye Başlat ----
  const handleStartPeriodEndReview = useCallback(async (row) => {
    await startPeriodEndReview(row.id);
  }, [startPeriodEndReview]);

  // ---- Dönem Sonu Değerlendirmeyi Tamamla ----
  const openCompleteReviewModal = useCallback((row) => {
    setEmployeeReviewNote('');
    setManagerReviewNote('');
    setCompleteReviewModal({ isOpen: true, planId: row.id, title: row.title });
  }, []);

  const handleCompleteReviewConfirm = useCallback(async () => {
    if (!completeReviewModal.planId || !employeeReviewNote.trim() || !managerReviewNote.trim()) return;
    setCompleteReviewBusy(true);
    await completeReview(completeReviewModal.planId, { employeeReviewNote, managerReviewNote });
    setCompleteReviewModal({ isOpen: false, planId: null, title: '' });
    setCompleteReviewBusy(false);
  }, [completeReviewModal.planId, employeeReviewNote, managerReviewNote, completeReview]);

  // ---- İK Daveti ----
  const handleInviteConfirm = useCallback(async () => {
    if (inviteEmployeeIds.length === 0) return;
    setInviteBusy(true);
    const ok = await invite(inviteEmployeeIds);
    if (ok) {
      setInviteModal(false);
      setInviteEmployeeIds([]);
    }
    setInviteBusy(false);
  }, [inviteEmployeeIds, invite]);

  const buildRowActions = useCallback((row) => {
    const items = [
      { type: 'detail', label: t('performance.idp.viewDetail'), onClick: () => openDetailModal(row) },
    ];
    if (!canManage) return items;

    if (row.status === 'meeting') {
      items.push({ type: 'divider' });
      items.push({ type: 'recordMeeting', label: t('performance.idp.recordMeeting'), onClick: () => openMeetingModal(row) });
    }
    if (row.status === 'manager_approval') {
      items.push({ type: 'divider' });
      items.push({ type: 'approve', label: t('performance.idp.approve'), onClick: () => handleApprove(row) });
      items.push({ type: 'requestRevision', label: t('performance.idp.requestRevision'), onClick: () => openRevisionModal(row) });
    }
    if (row.status === 'active') {
      items.push({ type: 'divider' });
      items.push({ type: 'startPeriodEndReview', label: t('performance.idp.startPeriodEndReview'), onClick: () => handleStartPeriodEndReview(row) });
    }
    if (row.status === 'period_end_review') {
      items.push({ type: 'divider' });
      items.push({ type: 'completeReview', label: t('performance.idp.completeReview'), onClick: () => openCompleteReviewModal(row) });
    }
    return items;
  }, [t, canManage, openDetailModal, openMeetingModal, handleApprove, openRevisionModal, handleStartPeriodEndReview, openCompleteReviewModal]);

  const pendingColumns = useMemo(() => [
    ...basePendingColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = pendingData[tableMeta.rowIndex];
          if (!row) return null;
          return <OptionsMenu actions={buildRowActions(row)} row={row} />;
        },
      },
    },
  ], [basePendingColumns, pendingData, buildRowActions]);

  const allColumns = useMemo(() => [
    ...baseColumns,
    {
      name: 'readyForReviewBadge',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row?.readyForReview) return null;
          return <ReadyBadge>{t('performance.idp.readyForReview')}</ReadyBadge>;
        },
      },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return <OptionsMenu actions={buildRowActions(row)} row={row} />;
        },
      },
    },
  ], [baseColumns, data, t, buildRowActions]);

  const tabs = useMemo(() => [
    { key: 'all', label: t('performance.idp.allPlans') },
    { key: 'pending', label: t('performance.idp.pending') },
  ], [t]);

  const detailPlan = detailModal.plan;

  return (
    <Container>
      {canManage && (
        <HeaderRow>
          <Button variant="outline" onClick={() => setInviteModal(true)}>
            {t('performance.idp.inviteEmployees')}
          </Button>
        </HeaderRow>
      )}

      {/* Detail modal (read-only) */}
      <Modal $open={detailModal.isOpen}>
        {detailModal.isOpen && (
          <ModalCard>
            <DialogTitle>{detailPlan?.title || '—'}</DialogTitle>
            {detailLoading ? (
              <div style={{ color: '#6c757d', fontSize: 13 }}>{t('common.loading')}...</div>
            ) : (
              <>
                <IdpFlowBar status={detailPlan?.status} />
                <DetailRow>
                  <DetailLabel>{t('performance.idp.employee')}:</DetailLabel>
                  <DetailValue>{detailPlan?.employeeName || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('performance.idp.cycle')}:</DetailLabel>
                  <DetailValue>{detailPlan?.cycleName || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{t('performance.idp.description')}:</DetailLabel>
                  <DetailValue>{detailPlan?.description || '-'}</DetailValue>
                </DetailRow>
                {/* P1 — kariyer hedefi (çalışan formdaki opsiyonel alan; boşsa satır gizli). */}
                {detailPlan?.careerGoal && (
                  <DetailRow>
                    <DetailLabel>{t('performance.idp.careerGoal')}:</DetailLabel>
                    <DetailValue>{detailPlan.careerGoal}</DetailValue>
                  </DetailRow>
                )}
                <DetailRow>
                  <DetailLabel>{t('performance.idp.overallProgress')}:</DetailLabel>
                  <DetailValue>
                    {detailPlan?.overallProgress != null ? `${detailPlan.overallProgress}%` : '-'}
                    {detailPlan?.readyForReview && <ReadyBadge>{t('performance.idp.readyForReview')}</ReadyBadge>}
                  </DetailValue>
                </DetailRow>
                {detailPlan?.managerSupport && (
                  <DetailRow>
                    <DetailLabel>{t('performance.idp.managerSupport')}:</DetailLabel>
                    <DetailValue>{detailPlan.managerSupport}</DetailValue>
                  </DetailRow>
                )}
                {detailPlan?.managerNote && (
                  <DetailRow>
                    <DetailLabel>{t('performance.idp.managerNotes')}:</DetailLabel>
                    <DetailValue>{detailPlan.managerNote}</DetailValue>
                  </DetailRow>
                )}
                {detailPlan?.status === 'completed' && (
                  <>
                    <DetailRow>
                      <DetailLabel>{t('performance.idp.employeeReviewNote')}:</DetailLabel>
                      <DetailValue>{detailPlan.employeeReviewNote || '-'}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('performance.idp.managerReviewNote')}:</DetailLabel>
                      <DetailValue>{detailPlan.managerReviewNote || '-'}</DetailValue>
                    </DetailRow>
                  </>
                )}

                {(detailPlan?.goals || []).map((g) => (
                  <GoalCard key={g.id}>
                    <div>
                      <CategoryBadge>{t(`performance.idp.category.${g.category}`, { defaultValue: g.category })}</CategoryBadge>
                      <strong>{g.title}</strong>
                    </div>
                    {g.successCriteria && <DetailValue style={{ display: 'block', marginTop: 6 }}>{g.successCriteria}</DetailValue>}
                    {(g.actions || []).map((a) => (
                      <ActionCard key={a.id}>
                        <div style={{ fontWeight: 600 }}>{a.actionTitle}</div>
                        <div style={{ color: '#6c757d', marginTop: 2, fontSize: 12 }}>
                          {t(`performance.idp.actionType.${a.actionType}`, { defaultValue: a.actionType })}
                          {a.targetDate && ` · ${formatDate(a.targetDate, i18n.language)}`}
                          {' · '}{t(`performance.idp.actionStatus.${a.status}`, { defaultValue: a.status })}
                          {a.progressPercent != null && ` · ${a.progressPercent}%`}
                        </div>
                      </ActionCard>
                    ))}
                  </GoalCard>
                ))}

                {/* TD-30 — hedefe bağlı OLMAYAN aksiyonlar. İlerleme ortalamasına zaten
                    giriyorlardı ama hiçbir yerde görünmüyorlardı; yönetici de planı
                    "neden %100 değil?" diye okuyamıyordu. */}
                {(detailPlan?.generalActions || []).length > 0 && (
                  <GoalCard>
                    <div><strong>{t('performance.idp.generalActions')}</strong></div>
                    {detailPlan.generalActions.map((a) => (
                      <ActionCard key={a.id}>
                        <div style={{ fontWeight: 600 }}>{a.actionTitle}</div>
                        <div style={{ color: '#6c757d', marginTop: 2, fontSize: 12 }}>
                          {t(`performance.idp.actionType.${a.actionType}`, { defaultValue: a.actionType })}
                          {a.targetDate && ` · ${formatDate(a.targetDate, i18n.language)}`}
                          {' · '}{t(`performance.idp.actionStatus.${a.status}`, { defaultValue: a.status })}
                          {a.progressPercent != null && ` · ${a.progressPercent}%`}
                        </div>
                      </ActionCard>
                    ))}
                  </GoalCard>
                )}
              </>
            )}
            <Actions>
              <Button variant="outline" onClick={() => { setDetailModal({ isOpen: false, plan: null }); clearDeepLink(); }}>
                {t('common.close')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {/* Görüşmeyi Kaydet */}
      <Modal $open={meetingModal.isOpen}>
        {meetingModal.isOpen && (
          <ModalCard>
            <DialogTitle>{t('performance.idp.recordMeeting')} — {meetingModal.title}</DialogTitle>
            <FieldGroup>
              <TextInput
                label={t('performance.idp.managerSupport')}
                type="textarea"
                rows={3}
                value={managerSupport}
                onChange={(e) => setManagerSupport(e.target.value)}
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => setMeetingModal({ isOpen: false, planId: null, title: '' })}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleMeetingConfirm} disabled={meetingBusy} loading={meetingBusy}>
                {t('common.confirm')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {/* Revizyon İste */}
      <Modal $open={revisionModal.isOpen}>
        {revisionModal.isOpen && (
          <ModalCard>
            <DialogTitle>{t('performance.idp.requestRevision')} — {revisionModal.title}</DialogTitle>
            <FieldGroup>
              <TextInput
                label={t('performance.idp.revisionNote')}
                type="textarea"
                rows={3}
                value={revisionNote}
                onChange={(e) => setRevisionNote(e.target.value)}
                required
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => setRevisionModal({ isOpen: false, planId: null, title: '' })}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleRevisionConfirm}
                disabled={revisionBusy || !revisionNote.trim()}
                loading={revisionBusy}
              >
                {t('common.confirm')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {/* Dönem Sonu Değerlendirmeyi Tamamla */}
      <Modal $open={completeReviewModal.isOpen}>
        {completeReviewModal.isOpen && (
          <ModalCard>
            <DialogTitle>{t('performance.idp.completeReview')} — {completeReviewModal.title}</DialogTitle>
            <FieldGroup>
              <TextInput
                label={t('performance.idp.employeeReviewNote')}
                type="textarea"
                rows={3}
                value={employeeReviewNote}
                onChange={(e) => setEmployeeReviewNote(e.target.value)}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.idp.managerReviewNote')}
                type="textarea"
                rows={3}
                value={managerReviewNote}
                onChange={(e) => setManagerReviewNote(e.target.value)}
                required
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => setCompleteReviewModal({ isOpen: false, planId: null, title: '' })}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleCompleteReviewConfirm}
                disabled={completeReviewBusy || !employeeReviewNote.trim() || !managerReviewNote.trim()}
                loading={completeReviewBusy}
              >
                {t('common.confirm')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {/* İK Daveti */}
      <Modal $open={inviteModal}>
        {inviteModal && (
          <ModalCard>
            <DialogTitle>{t('performance.idp.inviteEmployees')}</DialogTitle>
            <FieldGroup>
              <AsyncGenericSelect
                label={t('performance.idp.employee')}
                value={inviteEmployeeIds}
                onChange={setInviteEmployeeIds}
                onSearch={employeeAsync.onSearch}
                onGetById={employeeAsync.onGetById}
                isMulti
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={() => { setInviteModal(false); setInviteEmployeeIds([]); }}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleInviteConfirm} disabled={inviteEmployeeIds.length === 0 || inviteBusy} loading={inviteBusy}>
                {t('common.confirm')}
              </Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      <div style={{ display: activeTab === 'all' ? 'block' : 'none' }}>
        {/* TD-23 — başarısız okuma boş tabloya DEĞİL, uyarıya düşer. */}
        {loadFailed && <LoadErrorBanner />}
        <DataTable
          title={t('performance.idp.managerTitle')}
          fetching={fetching}
          columns={allColumns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      </div>

      <div style={{ display: activeTab === 'pending' ? 'block' : 'none' }}>
        {pendingLoadFailed && <LoadErrorBanner />}
        <DataTable
          title={t('performance.idp.pending')}
          fetching={fetchingPending}
          columns={pendingColumns}
          data={pendingData}
          serverSide={false}
          pageSize={10}
        />
      </div>
    </Container>
  );
}
